import React from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { formatDay, reasonEntries } from '../../lib/overOrder';

/**
 * "Above your best day" review panel.
 *
 * Shown whenever a line passes the client's own ceiling. It never blocks the
 * order: it names the figures, offers the two quantities that would clear it,
 * and — for a client whose approval gate is on — asks for a reason and says
 * plainly what the hold means.
 *
 * Shared by client checkout and the admin create-on-behalf form.
 */
export const OverCapPanel = ({
  lines,
  gated = false,
  reasons,
  reasonCode,
  onReasonChange,
  note,
  onNoteChange,
  onSetQty,
  audience = 'client',
  reasonMissing = false,
}) => {
  if (!lines || lines.length === 0) return null;

  const who = audience === 'admin' ? "this client's" : 'your';
  const total = lines.reduce((sum, l) => sum + l.over_by, 0);

  return (
    <div
      className="rounded-xl border border-orange-200 bg-orange-50/60 shadow-sm overflow-hidden"
      data-testid="over-cap-panel"
    >
      <div className="flex items-start gap-3 px-4 py-3 border-b border-orange-200 bg-orange-100/60">
        <AlertTriangle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-orange-900">
            {lines.length} line{lines.length === 1 ? '' : 's'} above {who} best day
            <span className="font-normal"> · {total} unit{total === 1 ? '' : 's'} over</span>
          </p>
          <p className="text-xs text-orange-800/80 mt-0.5">
            {audience === 'admin'
              ? 'The ceiling is the most this client has ever sold in a single delivery, plus an allowance. Nothing here stops the order.'
              : 'The ceiling is the most you have ever sold in a single delivery, plus an allowance. You can still order it.'}
          </p>
        </div>
      </div>

      <div className="divide-y divide-orange-100">
        {lines.map((line) => (
          <div key={line.product_id} className="px-4 py-3" data-testid={`over-cap-line-${line.product_id}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-gray-900 truncate">{line.product_name}</p>
              <p className="text-sm font-semibold text-orange-700 shrink-0">
                {line.ordered_qty} <span className="font-normal text-orange-600/80">(+{line.over_by})</span>
              </p>
            </div>
            <dl className="mt-1.5 grid grid-cols-3 gap-2 text-xs">
              <div>
                <dt className="text-gray-500">Best day</dt>
                <dd className="font-semibold text-gray-900">
                  {line.peak_sold}
                  {line.peak_date && (
                    <span className="font-normal text-gray-500"> · {formatDay(line.peak_date)}</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Ceiling</dt>
                <dd className="font-semibold text-gray-900">{line.cap_qty}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Usually sells</dt>
                <dd className="font-semibold text-gray-900">
                  {line.avg_sold ?? '—'}
                  {line.sell_through_pct != null && (
                    <span className="font-normal text-gray-500"> · {line.sell_through_pct}% sold</span>
                  )}
                </dd>
              </div>
            </dl>
            {onSetQty && (
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onSetQty(line.product_id, line.cap_qty)}
                  className="rounded-full border border-orange-300 bg-white px-2.5 py-1 text-xs font-medium text-orange-800 hover:bg-orange-100 transition-colors"
                  data-testid={`use-cap-${line.product_id}`}
                >
                  Use the cap ({line.cap_qty})
                </button>
                {line.suggested_qty != null && line.suggested_qty !== line.cap_qty && (
                  <button
                    type="button"
                    onClick={() => onSetQty(line.product_id, line.suggested_qty)}
                    className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                    data-testid={`use-suggested-cap-${line.product_id}`}
                  >
                    Use suggested ({line.suggested_qty})
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {gated && (
        <div className="px-4 py-3 border-t border-orange-200 bg-white/70 space-y-3">
          <div className="flex items-start gap-2">
            <Lock className="h-4 w-4 text-orange-600 shrink-0 mt-0.5" />
            <p className="text-xs text-orange-900">
              {audience === 'admin'
                ? 'Approval is switched on for this account, so a reason is required. An order the bakery books is never held.'
                : 'Approval applies to your account at the moment, so this order waits with us until we confirm it — we decide before the ordering deadline for that date, and you will get an email either way.'}
            </p>
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-700">
              Why keep {audience === 'admin' ? 'these quantities' : 'this quantity'}? *
            </Label>
            <Select value={reasonCode || ''} onValueChange={onReasonChange}>
              <SelectTrigger
                className={`mt-1 bg-white ${reasonMissing ? 'border-red-400 ring-1 ring-red-200' : ''}`}
                data-testid="over-cap-reason-select"
              >
                <SelectValue placeholder="Pick a reason..." />
              </SelectTrigger>
              <SelectContent>
                {reasonEntries(reasons).map(([code, label]) => (
                  <SelectItem key={code} value={code} data-testid={`over-cap-reason-${code}`}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {reasonMissing && (
              <p className="mt-1 text-xs text-red-600" data-testid="over-cap-reason-error">
                Pick a reason, or drop the quantities to the cap.
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-700">Anything we should know? (optional)</Label>
            <Textarea
              value={note || ''}
              onChange={(e) => onNoteChange?.(e.target.value)}
              placeholder="e.g. street festival on the Saturday"
              rows={2}
              className="mt-1 bg-white text-sm"
              data-testid="over-cap-note-input"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OverCapPanel;
